import styled from "@emotion/styled";
import { MenuItem, Select, Typography } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { ReactComponent as CheckIcon } from "Components/_Shared/Icons/Assets/check.svg";
import { ReactComponent as DownButton } from "Components/_Shared/Icons/Assets/down-button.svg";
import { ReactComponent as ProgressIcon } from "Components/_Shared/Icons/Assets/progress.svg";
import { ReactComponent as UnavailableIcon } from "Components/_Shared/Icons/Assets/unavailable.svg";
import { AnalysisDetailState } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";

const StyledSelect = styled(Select, {
  shouldForwardProp: isPropValid,
})<{ $readOnly: boolean }>`

  height: ${(props) => props.theme.spacing(7)};
  border-radius:  ${(props) => props.theme.spacing(Radius.MAX)};
  font-size: 14px;
  outline: 0;
  border: 0;
  padding: ${(props) => props.theme.spacing(0, Spacing.M)};

  .MuiTypography-root {
    font-weight: 500;
    line-height: 2.4;
  }

  .MuiInputBase-root {
    max-width: fit-content;
  }

  .MuiOutlinedInput-notchedOutline {
    border: 0;
  }

  .MuiSelect-select {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }

  .MuiList-root {
    padding: ${(props) => props.theme.spacing(Spacing.S)};
  }

  .MuiSelect-icon {
    right: 12px;
    top: 6px;
  }

  .MuiSelect-select {
    padding-right: ${(props) => (props.$readOnly ? 0 : "28px")} !important;
    cursor: ${(props) => (props.$readOnly ? "default" : "pointer")};
  }
`;

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: isPropValid,
})<{ $selected: boolean }>`
  display: ${(props) => (props.$selected ? "none" : "block")};
  border-radius: ${(props) => props.theme.spacing(Radius.S)};

  &.Mui-selected {
    background: transparent;
  }

  &.Mui-selected:hover, &.Mui-focusVisible {
    background: rgba(0, 0, 0, 0.04) !important;
  }
`;

type Props = {
  readonly state: AnalysisDetailState;
  readonly onStateChange: (state: AnalysisDetailState) => void;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
};

type VariantMap = {
  [key in AnalysisDetailState]: {
    readonly text: string;
    readonly color: string;
    readonly backgroundColor: string;
    readonly iconColor: string;
    readonly icon: React.ReactNode;
  };
};

export const AnalysisStatusSelect: React.FC<Props> = ({ state, onStateChange, disabled, readOnly = false }) => {
  const { t } = useResource();
  const variantMap: VariantMap = {
    [AnalysisDetailState.in_progress]: {
      text: t(Resources.AnalysesListing.Status.InProgress),
      color: "info.main",
      backgroundColor: "info.light",
      iconColor: "info.dark",
      icon: <ProgressIcon width={16} height={16} />,
    },
    [AnalysisDetailState.completed]: {
      text: t(Resources.AnalysesListing.Status.Completed),
      color: "success.main",
      backgroundColor: "success.light",
      iconColor: "success.main",
      icon: <CheckIcon width={16} height={16} />,
    },
    [AnalysisDetailState.declined]: {
      text: t(Resources.AnalysesListing.Status.Declined),
      color: "warning.dark",
      backgroundColor: "warning.light",
      iconColor: "warning.dark",
      icon: <UnavailableIcon width={16} height={16} />,
    },
  };

  return (
    <StyledSelect
      disabled={disabled}
      readOnly={readOnly}
      $readOnly={readOnly}
      onChange={(event) => onStateChange(event.target.value as AnalysisDetailState)}
      value={state}
      sx={{
        color: variantMap[state].color,
        backgroundColor: variantMap[state].backgroundColor,
      }}
      MenuProps={{
        slotProps: {
          paper: {
            sx: {
              paddingX: Spacing.S,
            },
          },
        },
      }}
      IconComponent={(props) =>
        readOnly ? null : (
          <Flex {...props} alignItems="center" color={variantMap[state].color} sx={{ cursor: "pointer" }} ml={Spacing.XXL}>
            <DownButton />
          </Flex>
        )
      }
    >
      {Object.values(AnalysisDetailState).map((value) => {
        return (
          <StyledMenuItem key={value} value={value} $selected={value === state}>
            <Flex alignItems="center">
              <Flex alignItems="center" color={variantMap[value].iconColor} mr={Spacing.M}>
                {variantMap[value].icon}
              </Flex>
              <Typography lineHeight={1.4} variant="body2" sx={{ color: variantMap[value].color }}>
                {variantMap[value].text}
              </Typography>
            </Flex>
          </StyledMenuItem>
        );
      })}
    </StyledSelect>
  );
};
